import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, formatINR } from "@/lib/api";
import { Stethoscope, Star, ShieldCheck, Languages, MapPin, ArrowRight, GraduationCap } from "lucide-react";

export default function Practitioners() {
  const [pracs, setPracs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http.get("/practitioners", { params: { status: "approved" } })
      .then((r) => setPracs(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="practitioners-page">
      <div className="mb-10 grid gap-6 md:grid-cols-[2fr_1fr] items-end">
        <div>
          <span className="badge-sage inline-flex items-center gap-1"><Stethoscope className="w-3 h-3" /> Verified BAMS Practitioners</span>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl font-bold text-[#1A201C] tracking-tight">
            Consult a licensed Ayurvedic doctor
          </h1>
          <p className="mt-3 text-[#5A6B5D] max-w-2xl">
            Every practitioner is verified for BAMS degree and active NCISM / State Council registration. Book a
            30-minute private consultation over video, phone or in-clinic — reviewed and moderated by Amiroop Healthion LLP.
          </p>
        </div>
        <div className="bg-[#1E3A2B] text-[#FAF8F5] rounded-2xl p-5">
          <div className="text-xs font-mono uppercase text-[#D97706]">Are you a BAMS practitioner?</div>
          <p className="text-sm mt-2 opacity-90">Set your own fee, own your schedule. Amiroop Healthion LLP takes just 20% platform commission.</p>
          <Link to="/practitioner/join" className="mt-4 inline-flex btn-amber text-sm" data-testid="practitioner-join-cta">Apply to join →</Link>
        </div>
      </div>

      {loading ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(3)].map((_, i) => <div key={i} className="aspect-[3/4] bg-[#E8F0EA] rounded-2xl animate-pulse" />)}
        </div>
      ) : pracs.length === 0 ? (
        <div className="text-center py-20 text-[#5A6B5D]">No approved practitioners just yet.</div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {pracs.map((p) => <PractitionerCard key={p.id} practitioner={p} />)}
        </div>
      )}
    </div>
  );
}

function PractitionerCard({ practitioner }) {
  const p = practitioner;
  return (
    <Link to={`/practitioner/${p.id}`} className="card-warm overflow-hidden group flex flex-col" data-testid={`practitioner-card-${p.id}`}>
      <div className="relative aspect-[4/3] bg-[#E8F0EA]">
        {p.photo_url ? (
          <img src={p.photo_url} alt={p.full_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <GraduationCap className="w-12 h-12 text-[#1E3A2B] opacity-30" />
          </div>
        )}
        <div className="absolute top-3 left-3 bg-white/95 border border-[#E2DDD5] rounded-full px-3 py-1 text-[10px] font-mono uppercase text-[#1E3A2B] flex items-center gap-1">
          <ShieldCheck className="w-3 h-3" /> {p.qualification || "Verified BAMS"}
        </div>
        <div className="absolute bottom-3 right-3 bg-[#1E3A2B] text-white rounded-full px-3 py-1 text-xs font-mono">
          {formatINR(p.consultation_fee)} <span className="opacity-70">/ {p.consultation_duration_min}m</span>
        </div>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center gap-1 text-xs text-[#D97706]">
          <Star className="w-3.5 h-3.5 fill-current" />
          <span className="font-mono text-[#1A201C]">{p.rating || "New"}</span>
          {p.reviews_count > 0 && <span className="text-[#5A6B5D]">({p.reviews_count} reviews)</span>}
        </div>
        <h3 className="mt-2 font-serif text-2xl font-semibold text-[#1A201C] leading-tight">
          {p.display_title} {p.full_name}
        </h3>
        <div className="mt-1 text-xs font-mono text-[#5A6B5D] flex items-center gap-1">
          <MapPin className="w-3 h-3" /> {p.clinic_city} · {p.years_experience}+ yrs experience
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {(p.specializations || []).slice(0, 3).map((s) => (
            <span key={s} className="badge-sage text-[10px]" style={{ background: "#E8F0EA" }}>{s}</span>
          ))}
        </div>
        <div className="mt-3 text-xs text-[#5A6B5D] flex items-center gap-1">
          <Languages className="w-3 h-3" /> {(p.languages || []).join(" · ")}
        </div>
        <div className="mt-4 pt-4 border-t border-[#E2DDD5] text-sm font-semibold text-[#1E3A2B] flex items-center gap-1">
          View profile & book <ArrowRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </Link>
  );
}
